import dayjs from 'dayjs';
import { z } from 'zod';
import {
  DBClassClassIdSchema,
  DBClassStreamsSchema,
  DBClassSubjectClassIdSchema,
  DBClassSubjectSchema,
} from '../classes/schemas';
import { DEFAULT_COUNTRY, DEFAULT_PROFILE_ICON } from '../common/constants';
import {
  AllCharLabelSchema,
  BirthdaySchema,
  CountrySchema,
  EmailSchema,
  GradYearSchema,
  HumanNameSchema,
  PhoneNumberSchema,
  PostcodeSchema,
  ProfileIconSchema,
  SearchStringSchema,
  SlackChannelIdSchema,
  SlackUserIdSchema,
  StreetAddressSchema,
  SubjectSchema,
  UnixTimestampSchema,
  XeroUserIdSchema,
} from '../common/schemas';
import { DBCourseCourseIdSchema, DBCourseSubjectCourseIdSchema } from '../courses/schemas';
import {
  DBSchoolLabelSchema,
  DBSchoolSchoolIdSchema,
  DBSchoolShortLabelSchema,
} from '../schools/schemas';
import { DBStreamStreamIdSchema, StreamStreamIdSchema } from '../streams/schemas';
import {
  DBTutorialCourseSchema,
  DBTutorialDurationSchema,
  DBTutorialStartDatetimeIdentifierSchema,
} from '../tutorials/schemas';
import { DEFAULT_USER_SETTINGS } from '../users/constants';
import { DBUserSharedSettingsSchema, UserIdSchema } from '../users/schemas';
import {
  AttendanceType,
  ContactOption,
  CourseEnrolmentState,
  DiscountType,
  StudentState,
} from './enums';

/**
 * Shared & Helper Schemas
 */

export const DBStudentCourseCreditsSchema = z.number().int();
export const DBStudentDiscountTypeSchema = z.enum(DiscountType);
export const DBStudentCourseStateStateSchema = z.enum(CourseEnrolmentState);
export const DBStudentCourseStateSchema = z.object({
  subject: SubjectSchema,
  course: z.lazy(() => DBCourseCourseIdSchema),
  state: DBStudentCourseStateStateSchema,
  termCredits: DBStudentCourseCreditsSchema,
  extraCredits: DBStudentCourseCreditsSchema,
  lessonsCompleted: z.number().int().min(0),
  firstLessonReminded: z.boolean(),
  absenceStreak: z.boolean(),
});

/**
 * DB Schemas
 */

export const DBStudentStudentIdSchema = z.lazy(() => UserIdSchema);
export const DBStudentAttendanceTypeSchema = z.enum(AttendanceType);
export const DBStudentBirthdaySchema = BirthdaySchema;
export const DBStudentClassCountSchema = z.number().int().min(0);
export const DBStudentClassesSchema = z.record(
  z.lazy(() => DBClassSubjectClassIdSchema),
  z.object({
    course: z.lazy(() => DBCourseCourseIdSchema),
    stream: z.lazy(() => DBStreamStreamIdSchema),
    // The class's stream per course, as `class.streams`; an entry stored without it reads
    // as holding the stream in use for its course
    streams: z.lazy(() => DBClassStreamsSchema).optional(),
  })
);
export const DBStudentCountrySchema = CountrySchema;
export const DBStudentCourseStatesSchema = z.record(
  z.lazy(() => DBCourseSubjectCourseIdSchema),
  DBStudentCourseStateSchema
);
export const DBStudentCreatedAtSchema = UnixTimestampSchema;
export const DBStudentDisabledSchema = z.boolean();
export const DBStudentDiscountsSchema = z
  .array(
    z.object({
      amount: z.number(),
      reason: AllCharLabelSchema.min(1).max(2048),
      stackable: z.boolean(),
      type: DBStudentDiscountTypeSchema,
    })
  )
  .refine(discounts => {
    // Check that reasons are unique
    const reasons = discounts.map(d => d.reason);
    return reasons.length === new Set(reasons).size;
  });
export const DBStudentEmailSchema = EmailSchema;
export const DBStudentFirstLessonRemindedSchema = z.boolean();
export const DBStudentFirstNameSchema = HumanNameSchema;
export const DBStudentGradYearSchema = GradYearSchema;
export const DBStudentInvoiceInstallmentCountSchema = z.number().int().min(1).max(12);
export const DBStudentLastNameSchema = HumanNameSchema;
export const DBStudentLessonsCompletedSchema = z.number().int().min(0);
export const DBStudentOnboardingSchema = z.object({
  /** When the student closed the first-login walkthrough, by finishing it or putting it off */
  finishedAt: UnixTimestampSchema.optional(),
  /** When the student said they had joined the Slack workspace, which nothing can check */
  slackJoinedAt: UnixTimestampSchema.optional(),
});
export const DBStudentParentEmailSchema = EmailSchema;
export const DBStudentParentFirstNameSchema = HumanNameSchema;
export const DBStudentParentLastNameSchema = HumanNameSchema;
export const DBStudentParentPhoneNumberSchema = PhoneNumberSchema;
export const DBStudentPhoneNumberSchema = PhoneNumberSchema;
export const DBStudentPostcodeSchema = PostcodeSchema;
export const DBStudentPreferredContactSchema = z.enum(ContactOption);
export const DBStudentProfileIconSchema = ProfileIconSchema;
export const DBStudentRequiresInvoiceSchema = z.boolean();
export const DBStudentRequiresRefundSchema = z.boolean();
export const DBStudentSchoolIdSchema = z.lazy(() => DBSchoolSchoolIdSchema);
export const DBStudentSchoolLabelSchema = z.lazy(() => DBSchoolLabelSchema);
export const DBStudentSchoolShortLabelSchema = z.lazy(() => DBSchoolShortLabelSchema);
export const DBStudentSearchStrSchema = SearchStringSchema;
export const DBStudentSettingsSchema = z.lazy(() => DBUserSharedSettingsSchema);
export const DBStudentSlackChannelsSchema = z.array(SlackChannelIdSchema);
export const DBStudentSlackIdSchema = SlackUserIdSchema;
export const DBStudentStateSchema = z.enum(StudentState);
export const DBStudentStreetAddressSchema = StreetAddressSchema;
export const DBStudentTutorialsSchema = z.record(
  z.lazy(() => DBTutorialStartDatetimeIdentifierSchema),
  z.object({
    duration: DBTutorialDurationSchema,
    course: DBTutorialCourseSchema,
    startDatetimeIdentifier: z.lazy(() => DBTutorialStartDatetimeIdentifierSchema),
  })
);
export const DBStudentWaitlistedClassesSchema = z.array(
  z.object({
    addedDatetime: UnixTimestampSchema,
    classId: z.lazy(() => DBClassClassIdSchema),
    subject: z.lazy(() => DBClassSubjectSchema),
  })
);
export const DBStudentXeroIdSchema = XeroUserIdSchema;

export const DBStudentSchema = z.object({
  studentId: DBStudentStudentIdSchema,
  attendanceType: DBStudentAttendanceTypeSchema,
  birthday: DBStudentBirthdaySchema.optional(),
  classCount: DBStudentClassCountSchema,
  // Optional to allow removal of a specific class
  classes: DBStudentClassesSchema.optional(),
  country: DBStudentCountrySchema,
  courseStates: DBStudentCourseStatesSchema,
  createdAt: DBStudentCreatedAtSchema,
  disabled: DBStudentDisabledSchema,
  discounts: DBStudentDiscountsSchema,
  email: DBStudentEmailSchema,
  firstLessonReminded: DBStudentFirstLessonRemindedSchema,
  firstName: DBStudentFirstNameSchema,
  gradYear: DBStudentGradYearSchema,
  invoiceInstallmentCount: DBStudentInvoiceInstallmentCountSchema,
  lastName: DBStudentLastNameSchema,
  lessonsCompleted: DBStudentLessonsCompletedSchema,
  onboarding: DBStudentOnboardingSchema.optional(),
  parentEmail: DBStudentParentEmailSchema.optional(),
  parentFirstName: DBStudentParentFirstNameSchema.optional(),
  parentLastName: DBStudentParentLastNameSchema.optional(),
  parentPhoneNumber: DBStudentParentPhoneNumberSchema.optional(),
  phoneNumber: DBStudentPhoneNumberSchema,
  postcode: DBStudentPostcodeSchema.optional(),
  preferredContact: DBStudentPreferredContactSchema,
  profileIcon: DBStudentProfileIconSchema,
  requiresInvoice: DBStudentRequiresInvoiceSchema,
  requiresRefund: DBStudentRequiresRefundSchema,
  // A student without a resolved schools-table reference carries neither attribute
  schoolId: DBStudentSchoolIdSchema.optional(),
  // Denormalised copies of the referenced school's label and short label, kept in
  // step with the schools table so student reads never need a second lookup
  schoolLabel: DBStudentSchoolLabelSchema.optional(),
  schoolShortLabel: DBStudentSchoolShortLabelSchema.optional(),
  searchStr: DBStudentSearchStrSchema,
  settings: DBStudentSettingsSchema,
  // Optional to allow removal of a specific slack channel
  slackChannels: DBStudentSlackChannelsSchema.optional(),
  slackId: DBStudentSlackIdSchema.optional(),
  state: DBStudentStateSchema,
  streetAddress: DBStudentStreetAddressSchema.optional(),
  // Optional to allow removal of a specific tutorial
  tutorials: DBStudentTutorialsSchema.optional(),
  // Optional to allow removal of a specific waitlisted class
  waitlistedClasses: DBStudentWaitlistedClassesSchema.optional(),
  xeroId: DBStudentXeroIdSchema.optional(),
});

/**
 * Client Schemas
 */

export const StudentStudentIdSchema = DBStudentStudentIdSchema;
export const StudentAttendanceTypeSchema = DBStudentAttendanceTypeSchema;
export const StudentBirthdaySchema = DBStudentBirthdaySchema;
export const StudentClassCountSchema = DBStudentClassCountSchema;
export const StudentClassesSchema = z.array(
  z.object({
    subject: z.lazy(() => DBClassSubjectSchema),
    classId: z.lazy(() => DBClassClassIdSchema),
    course: z.lazy(() => DBCourseCourseIdSchema),
    stream: z.lazy(() => DBStreamStreamIdSchema),
    streams: z.lazy(() => DBClassStreamsSchema).optional(),
  })
);
export const StudentCountrySchema = DBStudentCountrySchema;
export const StudentCourseStateSchema = z.object({
  subject: SubjectSchema,
  course: z.lazy(() => DBCourseCourseIdSchema),
  stream: z.lazy(() => StreamStreamIdSchema).optional(),
  state: DBStudentCourseStateStateSchema.default(CourseEnrolmentState.OnTrial),
  termCredits: DBStudentCourseCreditsSchema.default(0),
  extraCredits: DBStudentCourseCreditsSchema.default(0),
  lessonsCompleted: z.number().int().min(0).default(0),
  firstLessonReminded: z.boolean().default(false),
  absenceStreak: z.boolean().default(false),
});
export const StudentCourseStatesSchema = z.array(StudentCourseStateSchema);
export const StudentCreatedAtSchema = DBStudentCreatedAtSchema;
export const StudentDisabledSchema = DBStudentDisabledSchema;
export const StudentDiscountsSchema = z
  .array(
    z.object({
      amount: z.number().default(0),
      reason: AllCharLabelSchema.min(1).max(2048),
      stackable: z.boolean().default(false),
      type: DBStudentDiscountTypeSchema,
    })
  )
  .refine(discounts => {
    const reasons = discounts.map(d => d.reason);
    return reasons.length === new Set(reasons).size;
  });
export const StudentEmailSchema = DBStudentEmailSchema;
export const StudentFirstLessonRemindedSchema = DBStudentFirstLessonRemindedSchema;
export const StudentFirstNameSchema = DBStudentFirstNameSchema;
export const StudentGradYearSchema = DBStudentGradYearSchema;
export const StudentInvoiceInstallmentCountSchema = DBStudentInvoiceInstallmentCountSchema;
export const StudentLastNameSchema = DBStudentLastNameSchema;
export const StudentLessonsCompletedSchema = DBStudentLessonsCompletedSchema;
export const StudentOnboardingSchema = DBStudentOnboardingSchema;
export const StudentParentEmailSchema = DBStudentParentEmailSchema;
export const StudentParentFirstNameSchema = DBStudentParentFirstNameSchema;
export const StudentParentLastNameSchema = DBStudentParentLastNameSchema;
export const StudentParentPhoneNumberSchema = DBStudentParentPhoneNumberSchema;
export const StudentPhoneNumberSchema = DBStudentPhoneNumberSchema;
export const StudentPostcodeSchema = DBStudentPostcodeSchema;
export const StudentPreferredContactSchema = DBStudentPreferredContactSchema;
export const StudentProfileIconSchema = DBStudentProfileIconSchema;
export const StudentRequiresInvoiceSchema = DBStudentRequiresInvoiceSchema;
export const StudentRequiresRefundSchema = DBStudentRequiresRefundSchema;
export const StudentSchoolIdSchema = DBStudentSchoolIdSchema;
export const StudentSchoolLabelSchema = DBStudentSchoolLabelSchema;
export const StudentSchoolShortLabelSchema = DBStudentSchoolShortLabelSchema;
export const StudentSearchStrSchema = DBStudentSearchStrSchema;
export const StudentSettingsSchema = DBStudentSettingsSchema;
export const StudentSlackChannelsSchema = DBStudentSlackChannelsSchema;
export const StudentSlackIdSchema = DBStudentSlackIdSchema;
export const StudentStateSchema = DBStudentStateSchema;
export const StudentStreetAddressSchema = DBStudentStreetAddressSchema;
export const StudentTutorialsSchema = z.array(
  z.object({
    tutorialId: DBTutorialStartDatetimeIdentifierSchema,
    duration: DBTutorialDurationSchema,
    course: DBTutorialCourseSchema,
    startDatetime: UnixTimestampSchema,
  })
);
export const StudentWaitlistedClassesSchema = DBStudentWaitlistedClassesSchema;
export const StudentXeroIdSchema = DBStudentXeroIdSchema;

export const StudentSchema = z.object({
  studentId: StudentStudentIdSchema,
  attendanceType: StudentAttendanceTypeSchema.default(AttendanceType.InPerson),
  birthday: StudentBirthdaySchema.optional(),
  classCount: StudentClassCountSchema.default(0),
  classes: StudentClassesSchema.default([]),
  country: StudentCountrySchema.default(DEFAULT_COUNTRY),
  courseStates: StudentCourseStatesSchema.default([]),
  createdAt: StudentCreatedAtSchema.default(() => dayjs().valueOf()),
  disabled: StudentDisabledSchema.default(false),
  discounts: StudentDiscountsSchema.default([]),
  email: StudentEmailSchema,
  firstLessonReminded: StudentFirstLessonRemindedSchema.default(false),
  firstName: StudentFirstNameSchema,
  gradYear: StudentGradYearSchema,
  invoiceInstallmentCount: StudentInvoiceInstallmentCountSchema.default(1),
  lastName: StudentLastNameSchema,
  lessonsCompleted: StudentLessonsCompletedSchema.default(0),
  onboarding: StudentOnboardingSchema.optional(),
  parentEmail: StudentParentEmailSchema.optional(),
  parentFirstName: StudentParentFirstNameSchema.optional(),
  parentLastName: StudentParentLastNameSchema.optional(),
  parentPhoneNumber: StudentParentPhoneNumberSchema.optional(),
  phoneNumber: StudentPhoneNumberSchema,
  postcode: StudentPostcodeSchema.optional(),
  preferredContact: StudentPreferredContactSchema.default(ContactOption.Both),
  profileIcon: StudentProfileIconSchema.default(DEFAULT_PROFILE_ICON),
  requiresInvoice: StudentRequiresInvoiceSchema.default(false),
  requiresRefund: StudentRequiresRefundSchema.default(false),
  schoolId: StudentSchoolIdSchema.optional(),
  schoolLabel: StudentSchoolLabelSchema.optional(),
  schoolShortLabel: StudentSchoolShortLabelSchema.optional(),
  searchStr: StudentSearchStrSchema.default(''),
  settings: StudentSettingsSchema.default(DEFAULT_USER_SETTINGS),
  slackChannels: StudentSlackChannelsSchema.optional().default([]),
  slackId: StudentSlackIdSchema.optional(),
  state: StudentStateSchema.default(StudentState.Enrolled),
  streetAddress: StudentStreetAddressSchema.optional(),
  tutorials: StudentTutorialsSchema.optional().default([]),
  waitlistedClasses: StudentWaitlistedClassesSchema.optional().default([]),
  xeroId: StudentXeroIdSchema.optional(),
});
